/**
 * advance-missiles.ts — projectile + beam machinery for the v4M9
 * unified-flight-time combat model.
 *
 * Filename note (v4M9.7): the file is named `advance-missiles.ts` for
 * historical reasons — it started as v3M28's missile-as-persistent-
 * entity machinery and accumulated families across v4M9.2-.5 (Torpedo,
 * Pulse, Burst, Beam). The name is intentionally not renamed to
 * `advance-projectiles.ts`: 30+ import sites + risk under the bash-
 * mount-staleness gotcha (see parent CLAUDE.md gotcha #2) outweighs
 * the cosmetic gain. Treat this filename as a stable historical
 * marker; the contents are the canonical projectile + beam layer.
 *
 * Surfaces (post-v4M9.5):
 *
 *   - `enqueueMissile`, `enqueueTorpedo`, `enqueuePulse`, `enqueueBurst`
 *     — combat-dispatch enqueues. Each takes (state, attacker, target,
 *     weapon) and inserts one or more `Projectile` entries into
 *     `state.projectiles` with `family: Missile | Torpedo | Pulse | Burst`.
 *     Damage is deferred to the impact tick; accuracy frozen at fire.
 *   - `enqueueBeam` — inserts a `BeamFire` into `state.beams` for the
 *     sustained-channel mechanic (no projectile entity; per-tick damage
 *     during sustain window).
 *   - `advanceProjectiles` — per-tick walker over `state.projectiles`,
 *     resolves arrivals (any family), emits DamageEvents + impact VFX,
 *     despawns finished / orphaned entries.
 *   - `advanceBeams` — per-tick walker over `state.beams`, applies
 *     sustain damage during the active phase, despawns on expiry /
 *     abort-on-target-lost.
 *
 * All exports are pure of side effects beyond `state` mutation — no
 * DOM access, no audio access, no console output. Audio + render
 * layers read the resulting state each frame on their own.
 *
 * Design locks: `docs/PROJECTILES.md` (v4M9 canonical), `docs/MISSILES.md`
 * (v3M28 historical — superseded by PROJECTILES.md for the projectile
 * surfaces, still authoritative for Missile family specifics).
 */

import { nextFloat } from '../state/rng';
import { hitChance } from './combat';
import type {
  DamageEvent,
  GameState,
  Missile,
  ShipInstance,
  ShipTemplate,
  WeaponDef,
} from '../state/types/index';
import { damageTypeModifiers } from './combat-balance';
import { effectiveArmor, effectiveMaxHull } from './research-effects';
import { areTeammates } from './teams';
import {
  SQUADRON_FIGHTER_COUNT,
  updateSquadronFighterCount,
} from './balance-carrier-squadrons';

/**
 * Resolve a ship's template via the `${raceId}:${chassis}` key
 * convention. Private to this module — the same helper exists
 * inlined in `advance-combat.ts` / `advance-bombardment.ts` /
 * `advance-repair-yard.ts`; v3M28 keeps the duplication rather than
 * hoisting to a shared helper because the duplicates are 3 lines and
 * hoisting would touch four files needlessly.
 */
function getTemplate(state: GameState, ship: ShipInstance): ShipTemplate | undefined {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return undefined;
  return state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Per-family travel time in ticks. Identical within a family — per-Mk
 * differentiation lives in damage / range / accuracy, not TTL.
 *
 * Per `MISSILES.md §6`:
 *   - Pulson Missile Mk 1-3 → 30 ticks (3.0 s @ 10 Hz)
 *   - Zeon Missile Mk 4-6   → 40 ticks (4.0 s @ 10 Hz)
 *
 * Unrecognised weapon names default to the Pulson family via
 * `missileFamilyFromWeaponName` (and therefore the Pulson TTL) —
 * defensive fallback so a future Concussion-Missile weapon that
 * hasn't been added to the family table here still flies for a
 * sensible duration rather than crashing or sticking at infinity.
 */
export const PULSON_TTL_TICKS = 30;
export const ZEON_TTL_TICKS = 40;

/** Bezier control-point offset magnitude as a fraction of path length.
 *  Per `MISSILES.md §4.1`. Multiplied by the `state.rng`-rolled
 *  scatter factor `s ∈ [-1.5, 1.5]`. */
const ARC_DEPTH = 0.15;

/** Range of the per-missile scatter factor. The sign determines arc
 *  direction (above vs below the direct line); the magnitude
 *  determines arc depth. */
const SCATTER_MIN = -1.5;
const SCATTER_MAX = 1.5;

// ---------------------------------------------------------------------------
// Family + TTL lookup
// ---------------------------------------------------------------------------

/**
 * Resolve the missile family from a weapon name. Family drives TTL
 * (per §6) and per-family rendering / SFX (per §9–§10).
 *
 * Pulson Missile Mk 1-3 → 'pulson'; Zeon Missile Mk 4-6 → 'zeon'.
 * Unknown names default to 'pulson' so the missile still flies for
 * a sensible duration — see DEFAULT_TTL_TICKS.
 */
export function missileFamilyFromWeaponName(
  weaponName: string,
): 'pulson' | 'zeon' {
  if (weaponName.startsWith('Pulson')) return 'pulson';
  if (weaponName.startsWith('Zeon')) return 'zeon';
  return 'pulson';
}

/** TTL in ticks for a missile of the given family. */
export function missileTtlForFamily(family: 'pulson' | 'zeon'): number {
  return family === 'zeon' ? ZEON_TTL_TICKS : PULSON_TTL_TICKS;
}

// ---------------------------------------------------------------------------
// Bezier geometry
// ---------------------------------------------------------------------------

/**
 * Three-phase easing curve mapping `ageFrac ∈ [0, 1]` to eased `t ∈
 * [0, 1]` per `MISSILES.md §4.2`. Slow launch (0-15%, quad-ease-out
 * to ~7.5%), linear cruise (15-85%, 7.5% → 92.5%), slight terminal
 * deceleration (85-100%, 92.5% → 100%).
 *
 * Exported for testing — the advance pass uses this every tick to
 * recompute each in-flight missile's position from its Bezier
 * triple.
 */
export function easeMissile(ageFrac: number): number {
  const f = Math.max(0, Math.min(1, ageFrac));
  if (f < 0.15) {
    const local = f / 0.15;
    return local * local * 0.075;
  }
  if (f < 0.85) {
    const local = (f - 0.15) / 0.7;
    return 0.075 + local * 0.85;
  }
  const local = (f - 0.85) / 0.15;
  return 0.925 + local * 0.075;
}

/**
 * Evaluate the quadratic Bezier at parameter `t`. Returns
 * `{ x, y }` for `P0 = (sx0, sy0)`, `P1 = (sx1, sy1)`, `Pc = (cx, cy)`
 * per `MISSILES.md §4`.
 *
 * `t` here is the EASED parameter (output of `easeMissile`), not
 * the raw `ageFrac`. Callers should ease first.
 */
export function bezierAt(
  missile: Missile,
  t: number,
): { x: number; y: number } {
  const omt = 1 - t;
  const x =
    omt * omt * missile.sx0 +
    2 * omt * t * missile.cx +
    t * t * missile.sx1;
  const y =
    omt * omt * missile.sy0 +
    2 * omt * t * missile.cy +
    t * t * missile.sy1;
  return { x, y };
}

/**
 * Tangent of the quadratic Bezier at parameter `t` — used by the
 * renderer to rotate the missile sprite into its direction of
 * travel. Returns the raw (not-unit-normalised) tangent vector;
 * caller normalises if needed.
 */
export function bezierTangentAt(
  missile: Missile,
  t: number,
): { x: number; y: number } {
  const omt = 1 - t;
  const x =
    2 * omt * (missile.cx - missile.sx0) +
    2 * t * (missile.sx1 - missile.cx);
  const y =
    2 * omt * (missile.cy - missile.sy0) +
    2 * t * (missile.sy1 - missile.cy);
  return { x, y };
}

/**
 * Current eased position of an in-flight missile at game tick `tick`.
 * Convenience wrapper bundling the age-derivation + ease + Bezier
 * evaluation steps. Returns the spawn point at `tick === launchedAtTick`
 * and the target point at `tick >= launchedAtTick + ttlTicks`.
 */
export function missilePositionAt(
  missile: Missile,
  tick: number,
): { x: number; y: number } {
  const ageFrac = (tick - missile.launchedAtTick) / missile.ttlTicks;
  return bezierAt(missile, easeMissile(ageFrac));
}

// ---------------------------------------------------------------------------
// Launch — invoked from fireSalvo for Missile-subType weapons
// ---------------------------------------------------------------------------

/**
 * Push one Missile entity onto `state.projectiles`. Bezier scatter is
 * rolled via `state.rng` so save round-trips preserve trajectories.
 * Accuracy is also rolled at fire-time (`willHit` frozen here, not
 * at impact) — matches the existing accuracy-rolled-at-fire pattern
 * in every other weapon's instant-damage path.
 *
 * Caller is responsible for calling this exactly `count` times per
 * salvo (the count comes from the launching ship's per-slot weapon
 * loadout). Each invocation rolls its own scatter + accuracy.
 *
 * No damage applied here — that's deferred to the impact tick in
 * `advanceMissiles`.
 */
export function enqueueMissile(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
  /** MFix12.4 — inter-slot stagger offset. Combat dispatch passes
   *  `i * slotStaggerTicks` for slot i so successive instances of the
   *  same weapon on the firing ship launch staggered. Composes with
   *  the salvo-internal `staggerTicks`. Defaults to 0 (single-slot
   *  fire — no offset). */
  slotOffsetTicks: number = 0,
): void {
  // Bezier endpoints frozen at fire-time. The missile does NOT chase
  // a moving target — target movement during flight may cause a
  // visual miss even with `willHit === true`.
  const sx0 = attacker.sx;
  const sy0 = attacker.sy;
  const sx1 = target.sx;
  const sy1 = target.sy;

  // Perpendicular-offset midpoint, scaled by RNG-rolled scatter.
  const dx = sx1 - sx0;
  const dy = sy1 - sy0;
  const len = Math.hypot(dx, dy);
  // Unit perpendicular (90° CCW). Falls back to (0, 1) when launcher
  // and target are coincident (degenerate; shouldn't happen, but no
  // crash on edge cases).
  const ux = len > 0 ? -dy / len : 0;
  const uy = len > 0 ? dx / len : 1;
  const scatter = SCATTER_MIN + nextFloat(state.rng) * (SCATTER_MAX - SCATTER_MIN);
  const cx = (sx0 + sx1) / 2 + ux * len * ARC_DEPTH * scatter;
  const cy = (sy0 + sy1) / 2 + uy * len * ARC_DEPTH * scatter;

  // Accuracy roll frozen at fire-time. Uses the same hitChance math
  // as the instant-damage path — accuracy × (1 - dodge), rolled
  // against a uniform [0, 1).
  const targetTmpl = getTemplate(state, target);
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);
  const accRoll = nextFloat(state.rng);
  const willHit = accRoll < hitP;

  // `subFamily` here is the missile-internal Pulson/Zeon distinction
  // (drives TTL); the top-level `family: 'Missile'` discriminant on the
  // Projectile entity is the v4M9.2 cross-family selector (Missile vs
  // Torpedo vs Pulse vs Burst). The two are independent enums.
  const subFamily = missileFamilyFromWeaponName(weapon.name);
  const ttlTicks = missileTtlForFamily(subFamily);

  // MFix12.2 — `salvoSize` and `staggerTicks` from xlsx. Default 1 = no
  // behavioural change (single missile per fire-event-per-slot, as
  // before MFix12.2). Designers tune up for missile-pod-style multi-
  // round salvos. The pre-rolled `cx`, `cy`, and `willHit` above are
  // RE-rolled inside the loop per-round so each missile in a salvo
  // gets its own scatter + accuracy — partial-hit salvos possible.
  const mech = state.weaponMechanics.missile[weapon.name];
  const salvoSize = mech?.salvoSize ?? 1;
  const staggerTicks = mech?.staggerTicks ?? 1;

  for (let i = 0; i < salvoSize; i++) {
    // Re-roll scatter + accuracy per round. The first iteration
    // consumes the rolls already taken above (preserves single-shot
    // determinism); subsequent iterations roll fresh.
    let roundCx = cx;
    let roundCy = cy;
    let roundWillHit = willHit;
    if (i > 0) {
      const innerScatter =
        SCATTER_MIN + nextFloat(state.rng) * (SCATTER_MAX - SCATTER_MIN);
      roundCx = (sx0 + sx1) / 2 + ux * len * ARC_DEPTH * innerScatter;
      roundCy = (sy0 + sy1) / 2 + uy * len * ARC_DEPTH * innerScatter;
      const innerAccRoll = nextFloat(state.rng);
      roundWillHit = innerAccRoll < hitP;
    }

    const id = state.nextEntityId;
    state.nextEntityId = id + 1;
    state.projectiles.set(id, {
      family: 'Missile',
      id,
      ownerEmpireId: attacker.ownerEmpireId,
      launchedFromShipId: attacker.id,
      systemId: attacker.systemId ?? target.systemId ?? 0,
      targetShipId: target.id,
      weaponName: weapon.name,
      launchedAtTick: state.tick + slotOffsetTicks + i * staggerTicks,
      ttlTicks,
      sx0,
      sy0,
      sx1,
      sy1,
      cx: roundCx,
      cy: roundCy,
      willHit: roundWillHit,
      // MFix29.1 — Missile doesn't currently use per-slot perpendicular
      // spawn (Missile spawns from the firer centerline + Bezier
      // scatter handles visual variety). Defaults to 0; pulse is
      // currently the only family that consumes slotPerp at render.
      slotPerp: 0,
    });
  }
}

/**
 * v4M9.2 — Torpedo enqueue path. Structurally identical to
 * `enqueueMissile` (same Bezier scatter + accuracy-frozen-at-fire
 * pattern) — the differences are:
 *
 *   - `family: 'Torpedo'` discriminant on the resulting Projectile.
 *   - `ttlTicks` derived from launcher→target distance divided by
 *     `state.weaponMechanics.torpedo[weaponName].flightSpeedPerTick`,
 *     so per-Mk torpedo speed (slow class — 0.012-0.016 typical) is
 *     the authoritative dial. Missile uses fixed per-sub-family TTL
 *     instead; the design lock in `docs/PROJECTILES.md §3` distinguishes
 *     the two on purpose so Torpedo's slower flight reads visually.
 *   - Defensive fallback: missing mechanics entry → 40-tick TTL
 *     (matches the Zeon-tier missile default). Keeps the dispatch
 *     path safe before xlsx values land for every Mk-tier.
 *
 * Damage is deferred to the impact tick in `advanceMissiles` just like
 * the missile path — the per-tick walker is family-agnostic in v4M9.2;
 * family-specific impact specials (splash radius, point-defense) land
 * in v4M9.2.3 / v4M9.6.
 */
export function enqueueTorpedo(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
  /** MFix12.4 — inter-slot stagger offset; see enqueueMissile param doc. */
  slotOffsetTicks: number = 0,
): void {
  const sx0 = attacker.sx;
  const sy0 = attacker.sy;
  const sx1 = target.sx;
  const sy1 = target.sy;

  const dx = sx1 - sx0;
  const dy = sy1 - sy0;
  const len = Math.hypot(dx, dy);
  const ux = len > 0 ? -dy / len : 0;
  const uy = len > 0 ? dx / len : 1;
  const scatter = SCATTER_MIN + nextFloat(state.rng) * (SCATTER_MAX - SCATTER_MIN);
  const cx = (sx0 + sx1) / 2 + ux * len * ARC_DEPTH * scatter;
  const cy = (sy0 + sy1) / 2 + uy * len * ARC_DEPTH * scatter;

  const targetTmpl = getTemplate(state, target);
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);
  const accRoll = nextFloat(state.rng);
  const willHit = accRoll < hitP;

  // Per-tick speed sourced from the v4M9.0 xlsx pipeline. Defensive
  // fallback to a Zeon-equivalent 40-tick TTL if the entry is missing
  // (early-development weapon name not yet in the sheet).
  const mech = state.weaponMechanics.torpedo[weapon.name];
  const speed = mech?.flightSpeedPerTick ?? 0;
  const ttlTicks = speed > 0 ? Math.max(1, Math.ceil(len / speed)) : 40;

  // MFix12.2 — `salvoSize` / `staggerTicks` from xlsx. Default 1 = no
  // behavioural change (single torpedo per slot per fire-event), which
  // matches the design intent for Torpedo as the heavy single-launch
  // family. Each round in a >1-size salvo re-rolls scatter + accuracy
  // independently so partial-hit salvos are possible.
  const salvoSize = mech?.salvoSize ?? 1;
  const staggerTicks = mech?.staggerTicks ?? 1;

  for (let i = 0; i < salvoSize; i++) {
    let roundCx = cx;
    let roundCy = cy;
    let roundWillHit = willHit;
    if (i > 0) {
      const innerScatter =
        SCATTER_MIN + nextFloat(state.rng) * (SCATTER_MAX - SCATTER_MIN);
      roundCx = (sx0 + sx1) / 2 + ux * len * ARC_DEPTH * innerScatter;
      roundCy = (sy0 + sy1) / 2 + uy * len * ARC_DEPTH * innerScatter;
      const innerAccRoll = nextFloat(state.rng);
      roundWillHit = innerAccRoll < hitP;
    }

    const id = state.nextEntityId;
    state.nextEntityId = id + 1;
    state.projectiles.set(id, {
      family: 'Torpedo',
      id,
      ownerEmpireId: attacker.ownerEmpireId,
      launchedFromShipId: attacker.id,
      systemId: attacker.systemId ?? target.systemId ?? 0,
      targetShipId: target.id,
      weaponName: weapon.name,
      launchedAtTick: state.tick + slotOffsetTicks + i * staggerTicks,
      ttlTicks,
      sx0,
      sy0,
      sx1,
      sy1,
      slotPerp: 0,
      cx: roundCx,
      cy: roundCy,
      willHit: roundWillHit,
    });
  }
}

/**
 * v4M9.3 — Pulse enqueue path. Pulse is the highest-volume projectile
 * family (default ship weapon), so the implementation here keeps the
 * per-fire allocation cost trim:
 *
 *   - **No Bezier scatter.** Pulse rounds fly straight from launcher to
 *     target. The `cx, cy` fields are still populated (Projectile struct
 *     is shared across families) but set to the launcher→target midpoint
 *     so the Bezier evaluator collapses to a linear interpolation.
 *   - **No `state.rng` scatter roll.** Saves one `nextFloat` call per
 *     pulse-fire vs. the Missile/Torpedo paths. Accuracy `willHit` is
 *     still rolled (one `nextFloat`) — same accuracy-frozen-at-fire
 *     contract as every other projectile family.
 *   - **`family: 'Pulse'`** discriminant on the resulting Projectile.
 *   - **`ttlTicks`** derived from launcher→target distance divided by
 *     `state.weaponMechanics.pulse[weaponName].flightSpeedPerTick`,
 *     same pattern as Torpedo. Defensive fallback: 8-tick TTL if the
 *     mechanics row is missing — keeps the per-Mk variation in the
 *     xlsx but gives every Pulse weapon a sane default at fire time
 *     even on day-0 catalogue gaps.
 *
 * Damage is deferred to the impact tick in `advanceProjectiles`. No
 * splash; Pulse is direct-hit only.
 */
export function enqueuePulse(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
  /** MFix12.4 — inter-slot stagger offset; see enqueueMissile param doc. */
  slotOffsetTicks: number = 0,
  /** MFix29 — slot index within the loadout's Pulse count. Mirrors
   *  enqueueBurst/enqueueBeam. Used to derive the per-slot perpendicular
   *  spawn offset so multi-slot Pulse loadouts visibly fire from
   *  distinct emitter points on the ship. Single-slot weapons use 0. */
  slotIndex: number = 0,
): void {
  const sx0 = attacker.sx;
  const sy0 = attacker.sy;
  const sx1 = target.sx;
  const sy1 = target.sy;

  const targetTmpl = getTemplate(state, target);
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);

  const mech = state.weaponMechanics.pulse[weapon.name];
  const speed = mech?.flightSpeedPerTick ?? 0;
  const len = Math.hypot(sx1 - sx0, sy1 - sy0);
  const ttlTicks = speed > 0 ? Math.max(1, Math.ceil(len / speed)) : 8;

  // MFix12.2 — rounds-per-fire-event dial. Default 1 = single-round
  // pulse (unchanged from pre-MFix12.2). Designers tune up via xlsx
  // for the rapid-fire pulse-cannon feel; cooldown (rateOfFire) still
  // gates the inter-salvo pause.
  const salvoSize = mech?.salvoSize ?? 1;
  const staggerTicks = mech?.staggerTicks ?? 1;

  // MFix29 / MFix29.1 — per-slot perpendicular spawn offset (world
  // units), slightly wider than burst's 0.002: SLOT_STEP_WU = 0.003,
  // cap 0.009. Slot 0 → 0; slot 1 → +0.003; slot 2 → -0.003; slot 3
  // → +0.006; slot 4 → -0.006; slot 5+ capped at ±0.009.
  //
  // Stored on the projectile (NOT baked into sx0/sy0) so the
  // renderer can re-anchor the visible spawn to the firer's CURRENT
  // position each frame — otherwise the stored launch-time sx0
  // visibly lags behind a moving firer ("bolt fires from behind the
  // ship"). sx0/sy0 sit at the firer's centerline; render layer adds
  // `perp_unit × slotPerp` against the firer's current position.
  const SLOT_STEP_WU = 0.003;
  const SLOT_MAX_WU = 0.009;
  let slotPerp = 0;
  if (slotIndex > 0) {
    const slotMag = Math.min(SLOT_MAX_WU, Math.ceil(slotIndex / 2) * SLOT_STEP_WU);
    const slotSign = slotIndex % 2 === 1 ? 1 : -1;
    slotPerp = slotMag * slotSign;
  }

  // Per-round independent accuracy roll so partial-hit salvos are
  // possible against high-dodge targets. Stagger via progressive
  // launchedAtTick; the advance pass tolerates future tick values
  // naturally (negative ageTicks falls through the in-flight check).
  for (let i = 0; i < salvoSize; i++) {
    const accRoll = nextFloat(state.rng);
    const willHit = accRoll < hitP;

    const id = state.nextEntityId;
    state.nextEntityId = id + 1;
    state.projectiles.set(id, {
      family: 'Pulse',
      id,
      ownerEmpireId: attacker.ownerEmpireId,
      launchedFromShipId: attacker.id,
      systemId: attacker.systemId ?? target.systemId ?? 0,
      targetShipId: target.id,
      weaponName: weapon.name,
      launchedAtTick: state.tick + slotOffsetTicks + i * staggerTicks,
      ttlTicks,
      sx0,
      sy0,
      sx1,
      sy1,
      // Midpoint — collapses the Bezier evaluator to a straight line
      // for Pulse. No rng scatter cost (Pulse is high-volume).
      cx: (sx0 + sx1) / 2,
      cy: (sy0 + sy1) / 2,
      willHit,
      slotPerp,
    });
  }
}

/**
 * v4M9.4 — Burst enqueue path. One call from `fireSalvo` spawns
 * `burstSize` Projectile entries with progressive `launchedAtTick`
 * values — each subsequent round is delayed by `staggerTicks`. The
 * advance pass handles the staggered cohort naturally: rounds with
 * `state.tick < launchedAtTick` see negative `ageTicks` and skip the
 * in-flight check via the `ageTicks < ttlTicks` gate; renderer +
 * audio likewise guard on `ageFrac < 0`.
 *
 * Per-round detail:
 *
 *   - **Independent scatter.** Each round rolls its own scatter via
 *     `state.rng` × `offsetScatter` (the xlsx-driven magnitude dial).
 *     Curving stream feel: rounds within a burst arc differently so
 *     the visual reads as a gatling spray rather than a tight cluster.
 *   - **Independent accuracy roll.** Per-round `willHit` so a burst
 *     can partially hit a high-dodge target. Matches the legacy
 *     instant-hit model where each round in the volley rolled
 *     separately.
 *   - **Independent damage on arrival.** Each round applies
 *     `weapon.damage` (per-round damage) through the standard shield/
 *     armor/hull chain. Total burst damage = `burstSize × damage`
 *     (assuming all hit) before defensive math.
 *
 * Defensive fallbacks (xlsx row missing): burstSize=3, staggerTicks=1,
 * offsetScatter=0.5, flightSpeedPerTick=0.06 → ~8-tick TTL across
 * the fixture's launcher→target offset.
 */
export function enqueueBurst(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
  /** MFix12.4 — inter-slot stagger offset; see enqueueMissile param doc. */
  slotOffsetTicks: number = 0,
  /** MFix28 — slot index within the loadout's Burst count. Mirrors
   *  enqueueBeam's `slotIndex` parameter. Used to derive the per-slot
   *  perpendicular spawn offset so multi-slot Burst loadouts (e.g. a
   *  ship mounting 6× Gauss Autocannon) visibly fire from distinct
   *  gun ports on the ship rather than all rounds emerging from
   *  centerline. Single-slot weapons use 0 (no offset). */
  slotIndex: number = 0,
): void {
  const mech = state.weaponMechanics.burst[weapon.name];
  // MFix12.2 — `burstSize` renamed to `salvoSize` for cross-family
  // consistency (Pulse / Missile / Torpedo gained the same dial).
  // Schema docstring on `BurstMechanic` notes the rename.
  const burstSize = mech?.salvoSize ?? 3;
  const staggerTicks = mech?.staggerTicks ?? 1;
  // MFix28 — `offsetScatter` no longer consumed. Pre-MFix28 it
  // magnituded the per-round Bezier control-point displacement so
  // rounds visibly curved L/R of the firer→target axis; in playtest
  // this read as "burst is missing the target" rather than "spread
  // fire". The mech field stays in the xlsx for backward compat but
  // is intentionally unused here.
  const speed = mech?.flightSpeedPerTick ?? 0;

  const sx0 = attacker.sx;
  const sy0 = attacker.sy;
  const sx1 = target.sx;
  const sy1 = target.sy;

  const dx = sx1 - sx0;
  const dy = sy1 - sy0;
  const len = Math.hypot(dx, dy);
  const ux = len > 0 ? -dy / len : 0;
  const uy = len > 0 ? dx / len : 1;

  // TTL derived per-burst (same for every round in this fire — they
  // all travel the same launcher→target distance at the same speed).
  // Falls back to 8 ticks if the mechanics row is absent.
  const ttlTicks = speed > 0 ? Math.max(1, Math.ceil(len / speed)) : 8;

  const targetTmpl = getTemplate(state, target);
  const dodge = targetTmpl?.dodge ?? 0;
  const hitP = hitChance(weapon.accuracy, dodge);

  // MFix28 — per-slot perpendicular spawn offset (world units). Mirrors
  // `beamSlotPerpOffsetPx` in `vfx.ts` but in world coordinates rather
  // than screen pixels (Burst spawn lives in sim/world space; Beam
  // offset is applied at render time). Multi-slot Burst loadouts now
  // visibly fire from distinct gun ports.
  //
  // MFix28.2 — tightened from 0.005/0.015 after live observation that
  // launch points sat slightly off the ship hull. Now 0.002/0.006 so
  // slot offsets hug the visible ship sprite: slot 0 → 0; slot 1 →
  // +0.002 wu (~2 px); slot 2 → -0.002; slot 3 → +0.004; slot 4 →
  // -0.004; slot 5+ capped at ±0.006 (~6 px).
  const SLOT_STEP_WU = 0.002;
  const SLOT_MAX_WU = 0.006;
  let slotPerp = 0;
  if (slotIndex > 0) {
    const slotMag = Math.min(SLOT_MAX_WU, Math.ceil(slotIndex / 2) * SLOT_STEP_WU);
    const slotSign = slotIndex % 2 === 1 ? 1 : -1;
    slotPerp = slotMag * slotSign;
  }

  // Spawn each round in the burst. Accuracy rolled per round. Round
  // trajectory is a straight line from spawn to target (no Bezier
  // scatter post-MFix28).
  for (let i = 0; i < burstSize; i++) {
    // MFix28 — preserve RNG sequencing across the scatter removal:
    // pre-MFix28 the loop rolled `scatterMag = nextFloat(state.rng) *
    // offsetScatter` BEFORE the accuracy roll. Removing the roll
    // would shift every subsequent RNG-consuming pass in the same
    // tick (other weapons firing, AI decisions, etc.). Consume and
    // discard one float at the same loop position to keep replays
    // and seeded runs deterministic at the same offset.
    void nextFloat(state.rng);

    const accRoll = nextFloat(state.rng);
    const willHit = accRoll < hitP;

    // MFix28 — per-round-within-salvo sinusoidal cycling on top of the
    // per-slot offset. With 90° phase increment per round the spawn
    // point oscillates 0 → +d → 0 → -d → 0 → … reading as a rotating
    // gatling barrel at each gun port.
    // MFix28.2 — magnitude tightened 0.003 → 0.001 (~1 px) after live
    // observation so the cycle reads as fine jitter rather than visible
    // perpendicular spread.
    const ROUND_CYCLE_WU = 0.001;
    const roundPerp = Math.sin((i * Math.PI) / 2) * ROUND_CYCLE_WU;
    const perpOffset = slotPerp + roundPerp;

    const spawnSx0 = sx0 + ux * perpOffset;
    const spawnSy0 = sy0 + uy * perpOffset;

    // Straight-line trajectory: Bezier control point at the geometric
    // midpoint between spawn and target. Pre-MFix28 displaced this by
    // `len * ARC_DEPTH * (nextFloat * offsetScatter) * sign` which
    // produced the visible L/R curve. The Bezier model is retained
    // (every Projectile carries `cx, cy`) so the renderer needs no
    // structural change.
    const cx = (spawnSx0 + sx1) / 2;
    const cy = (spawnSy0 + sy1) / 2;

    const id = state.nextEntityId;
    state.nextEntityId = id + 1;
    state.projectiles.set(id, {
      family: 'Burst',
      id,
      ownerEmpireId: attacker.ownerEmpireId,
      launchedFromShipId: attacker.id,
      systemId: attacker.systemId ?? target.systemId ?? 0,
      targetShipId: target.id,
      weaponName: weapon.name,
      launchedAtTick: state.tick + slotOffsetTicks + i * staggerTicks,
      ttlTicks,
      sx0: spawnSx0,
      sy0: spawnSy0,
      sx1,
      sy1,
      cx,
      cy,
      willHit,
      // MFix29.1 — Burst bakes per-slot offset into sx0/sy0 directly
      // (kinetic rounds are small grey balls so the moving-firer lag
      // is hardly visible). slotPerp recorded for parity/schema
      // completeness; the pulse render is the only consumer today.
      slotPerp,
    });
  }
}

/**
 * v4M9.5 — Beam enqueue path. Beams are structurally distinct from
 * projectiles: no travel time, no Bezier trajectory, no flight-time
 * scatter — instead a sustained channel of damage from launcher to
 * target over `sustainTicks` (preceded by a visual-only `chargeUpTicks`
 * lead-in during which no damage applies). The entity lives in
 * `state.beams` (a `Map<number, BeamFire>` parallel to
 * `state.projectiles`), not in the projectile collection.
 *
 * Per-fire detail:
 *
 *   - **One `BeamFire` per call.** Combat dispatch passes `count` (the
 *     number of Beam-weapon instances on the firer); `fireSalvo` loops
 *     over count so each Beam slot gets its own BeamFire entry. Two
 *     Beam slots on one ship = two parallel channels on the same tick.
 *   - **No accuracy roll.** Beams hit by definition during sustain —
 *     they're continuous-fire, not discrete-shot. The legacy instant-
 *     hit model rolled accuracy per "round"; under the new model the
 *     entire sustain is one continuous hit. Per-Mk Dodge already
 *     factors into the Beam weapon's `damagePerTick` via the v4M9.0
 *     xlsx tune.
 *   - **No `state.rng` consumption.** Same rationale as Pulse's
 *     no-scatter path — keeps fire-time deterministic without rolling.
 *
 * Defensive fallback when the mechanics row is missing: chargeUpTicks=3,
 * sustainTicks=20, damagePerTick from `weapon.damage` scaled so the
 * total sustain damage matches a single legacy instant-hit shot
 * (weapon.damage ÷ sustainTicks). Keeps balance neutral on day-0
 * catalogue gaps.
 */
export function enqueueBeam(
  state: GameState,
  attacker: ShipInstance,
  target: ShipInstance,
  weapon: WeaponDef,
  /** MFix12.4 — inter-slot stagger offset. Shifts the beam's startTick
   *  so a multi-Beam ship's channels stagger rather than all activating
   *  on the same tick. Defaults to 0 (single-slot fire). */
  slotOffsetTicks: number = 0,
  /** MFix19 — slot index within the loadout's Beam count. The renderer
   *  maps this to a perpendicular offset so each channel appears to
   *  emanate from a distinct point on the firer rather than all
   *  channels collapsing to the same line. Defaults to 0 (single-slot
   *  fire — no offset). */
  slotIndex: number = 0,
): void {
  const mech = state.weaponMechanics.beam[weapon.name];
  const chargeUpTicks = mech?.chargeUpTicks ?? 3;
  const sustainTicks = mech?.sustainTicks ?? 20;
  // Damage-per-tick: xlsx is authoritative when present. Fallback
  // preserves the legacy instant-hit total damage: weapon.damage
  // spread evenly across the sustain window.
  const damagePerTick =
    mech?.damagePerTick ??
    (sustainTicks > 0 ? weapon.damage / sustainTicks : weapon.damage);

  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  state.beams.set(id, {
    id,
    firerShipId: attacker.id,
    targetShipId: target.id,
    ownerEmpireId: attacker.ownerEmpireId,
    systemId: attacker.systemId ?? target.systemId ?? 0,
    weaponName: weapon.name,
    startTick: state.tick + slotOffsetTicks,
    chargeUpTicks,
    sustainTicks,
    damagePerTick,
    slotIndex,
  });
}

// ---------------------------------------------------------------------------
// Per-tick advance pass — lands in v3M28.3
// ---------------------------------------------------------------------------

/**
 * v3M28.3 — walk every entry in `state.projectiles`, age them, validate
 * their targets, resolve impact on arrival, despawn finished entries.
 *
 * Iteration is in ascending id order for determinism (Map preserves
 * insertion order — but same-tick saves loaded into a fresh process
 * would replay in the same order, so explicit sort keeps things
 * predictable independent of Map internals). Despawns mutate the
 * Map during iteration; that's safe since we've materialised the id
 * list up front.
 *
 * Pipeline position: runs AFTER `advanceCombat` (so newly-launched
 * missiles get one tick of "age 0 in flight" before potentially
 * arriving) and BEFORE `advanceShipMovement` (so impacts apply to
 * the ship's pre-movement position — the same convention
 * `advanceSurfaceBatteries` uses).
 */
/**
 * v4M9.2 — renamed from `advanceMissiles` to `advanceProjectiles`. The
 * pass walks every entry in `state.projectiles` regardless of family
 * — Missile, Torpedo, and (post-v4M9.3-.4) Pulse / Burst all share
 * the same per-tick arrival mechanic. Family-specific impact specials
 * (Torpedo splash etc.) branch off the `family` discriminant inside
 * the arrival block.
 *
 * v4M9.2.6 — `advanceMissiles` alias retired; all call sites use
 * `advanceProjectiles` directly.
 */
export function advanceProjectiles(state: GameState): void {
  // v4M9.3 — iterate the Map directly. The prior implementation
  // materialised + sorted the id list as defensive determinism cover,
  // but: (a) Map iteration is insertion-ordered by spec and that order
  // survives save round-trips (serialise → array → deserialise rebuilds
  // in the same insertion sequence), and (b) damage application at the
  // arrival tick is order-independent — each projectile resolves
  // against its own target without reading sibling state. Skipping the
  // sort + array allocation saves O(n log n) per tick, which matters
  // at Pulse-at-scale (hundreds of in-flight projectiles per combat
  // tick at v4M9.3 once Pulse migrates to the projectile pipeline).
  //
  // In-place deletion during iteration is safe on Map: the spec
  // guarantees deleted keys aren't revisited and not-yet-visited
  // keys remain visible. New inserts during iteration also stay
  // visible — fine here because impact handlers don't enqueue new
  // projectiles (they only mutate the target ship + emit events).
  for (const [id, missile] of state.projectiles) {
    const ageTicks = state.tick - missile.launchedAtTick;

    // MFix29 / MFix29.2 — cancel any round whose target is dead,
    // missing, mid-jump, or in a different system. MFix29 originally
    // gated this on `ageTicks < 0` (pending rounds only) with an
    // explicit "already fired / in motion" carve-out for in-flight
    // rounds. Playtest finding (MFix29.2): on a Pulse salvo of
    // `salvoSize: 4, staggerTicks: 1` the i=0 round at the kill tick
    // and the i=1 round on the following tick both sit at
    // `ageTicks === 0` (in-flight by the prior gate) and still emerge
    // from the firer toward the dead ship — reads as "pulse keeps
    // firing after ship destroyed." Drop the carve-out: any round
    // (pending, just-launched, or mid-flight) whose target is gone
    // despawns immediately. Friendly-fire teammate flip is handled at
    // arrival only (teams are locked at game start in v4; the in-
    // flight check would never fire).
    const tgtCheck = state.ships.get(missile.targetShipId);
    if (
      !tgtCheck ||
      tgtCheck.currentHull <= 0 ||
      tgtCheck.systemId !== missile.systemId
    ) {
      state.projectiles.delete(id);
      continue;
    }

    if (ageTicks < missile.ttlTicks) continue; // still in flight

    // ── Arrival ──────────────────────────────────────────────────
    // MFix29.2 — target was already validated above (dead/missing/
    // different-system rejects despawn at the top of the loop), so
    // by this point `tgtCheck` is the live target. Alias to `target`
    // for the legacy damage-chain code below; the original arrival-
    // time re-lookup was redundant under the new universal gate.
    const target = tgtCheck;
    // v4M3.3 — friendly-fire block per `TEAMS.md §4` point 3.
    // Defensive belt-and-braces: in v4 teams are locked at game start
    // so a missile's target can't become a teammate mid-flight, but
    // a v5+ mid-game team-reassign mechanic would surface this edge.
    // pickTarget already filters teammates so the despawn-silent
    // path is rare-to-impossible today; the check costs ~1 Map lookup.
    if (areTeammates(state, missile.ownerEmpireId, target.ownerEmpireId)) {
      state.projectiles.delete(id);
      continue;
    }

    // Resolve weapon from the live catalogue. Defensive null check
    // for the unlikely case the weapon name was retired mid-flight
    // (catalogue is read-only in v1 but the check costs nothing).
    const weapon = state.weapons.get(missile.weaponName);
    if (!weapon) {
      state.projectiles.delete(id);
      continue;
    }

    // Miss outcome — `willHit` was rolled at fire-time. Still emit
    // the detonation VFX so the player sees the missile "fizzle" at
    // arrival. v3M28.4 lands the detonation VFX wiring; for now the
    // miss path is the same silent despawn as the hit path's
    // post-impact cleanup. The DamageEvent is suppressed because no
    // damage applied.
    if (!missile.willHit) {
      state.projectiles.delete(id);
      continue;
    }

    // ── Hit outcome — apply damage via the existing chain ────────
    const targetTmpl = getTemplate(state, target);
    const targetEmpire = state.empires.get(target.ownerEmpireId);
    const armor =
      targetTmpl && targetEmpire
        ? effectiveArmor(state, targetEmpire, targetTmpl)
        : targetTmpl?.armor ?? 0;
    const armorFactor = Math.max(0, Math.min(1, armor));
    const mods = damageTypeModifiers(state.balanceData)[weapon.damageType];

    const rawShieldHit = Math.min(weapon.damage, target.currentShields);
    const rawHullHit = Math.max(0, weapon.damage - rawShieldHit);
    const scaledShield = rawShieldHit * mods.shield;
    const scaledPostShield = rawHullHit * mods.hull;
    const hullDamage = scaledPostShield * (1 - armorFactor);

    target.currentShields = Math.max(0, target.currentShields - scaledShield);
    target.currentHull = Math.max(0, target.currentHull - hullDamage);

    // v4M9.2.6 — Torpedo splash. After the primary-target damage
    // applies, fan secondary damage out to every hostile ship within
    // `splashRadius` of the primary target's `(sx, sy)` in the same
    // system. Splash damage is 50 % of the weapon's nominal damage —
    // the splashDamageMult dial isn't in the xlsx schema yet; the
    // fixed 50 % keeps Torpedo's per-shot output bounded against
    // tight ship clusters without making splash strictly worse than
    // a no-splash double-tap (which would be a perverse design).
    // Primary target is excluded from the splash sweep so it doesn't
    // double-count damage.
    if (missile.family === 'Torpedo') {
      const mech = state.weaponMechanics.torpedo[weapon.name];
      const splashRadius = mech?.splashRadius ?? 0;
      if (splashRadius > 0) {
        const splashDamage = weapon.damage * 0.5;
        for (const splashTarget of state.ships.values()) {
          if (splashTarget.id === target.id) continue;
          if (splashTarget.systemId !== target.systemId) continue;
          if (splashTarget.currentHull <= 0) continue;
          if (areTeammates(state, missile.ownerEmpireId, splashTarget.ownerEmpireId)) continue;
          const dx = splashTarget.sx - target.sx;
          const dy = splashTarget.sy - target.sy;
          if (Math.hypot(dx, dy) > splashRadius) continue;

          // Mirror the primary damage chain (shield → armor-reduced
          // hull) so splash hits respect the same defensive math.
          const splashTmpl = getTemplate(state, splashTarget);
          const splashEmpire = state.empires.get(splashTarget.ownerEmpireId);
          const splashArmor =
            splashTmpl && splashEmpire
              ? effectiveArmor(state, splashEmpire, splashTmpl)
              : splashTmpl?.armor ?? 0;
          const splashArmorFactor = Math.max(0, Math.min(1, splashArmor));
          const splashRawShieldHit = Math.min(splashDamage, splashTarget.currentShields);
          const splashRawHullHit = Math.max(0, splashDamage - splashRawShieldHit);
          const splashScaledShield = splashRawShieldHit * mods.shield;
          const splashScaledPostShield = splashRawHullHit * mods.hull;
          const splashHullDamage = splashScaledPostShield * (1 - splashArmorFactor);

          splashTarget.currentShields = Math.max(
            0,
            splashTarget.currentShields - splashScaledShield,
          );
          splashTarget.currentHull = Math.max(
            0,
            splashTarget.currentHull - splashHullDamage,
          );

          // Squadron count recompute (mirrors the primary-hit branch).
          if (splashTarget.liveFighterCount !== undefined && splashTmpl && splashEmpire) {
            const fullPool = effectiveMaxHull(state, splashEmpire, splashTmpl);
            updateSquadronFighterCount(splashTarget, fullPool / SQUADRON_FIGHTER_COUNT);
          }

          // Floating-damage-number event for the splash hit. Same
          // damage-type gate as the primary; Torpedo is Concussion in
          // v1 so the gate always passes, but the check is defensive.
          if (
            splashTarget.systemId !== null &&
            (weapon.damageType === 'Energy' ||
              weapon.damageType === 'Kinetic' ||
              weapon.damageType === 'Concussion')
          ) {
            state.combatEvents.push({
              targetShipId: splashTarget.id,
              ownerEmpireId: missile.ownerEmpireId,
              systemId: splashTarget.systemId,
              sx: splashTarget.sx,
              sy: splashTarget.sy,
              shieldDamage: splashScaledShield,
              hullDamage: splashHullDamage,
              hitCount: 1,
              missCount: 0,
              damageType: weapon.damageType,
            });
          }
        }
      }
    }

    // v3M17.3 squadron variable-count HP pool — same recompute as
    // the ship-vs-ship damage chain in `advance-combat.ts`. Updates
    // `liveFighterCount` so the renderer immediately reflects the
    // post-impact squadron strength.
    if (target.liveFighterCount !== undefined && targetTmpl && targetEmpire) {
      const fullPool = effectiveMaxHull(state, targetEmpire, targetTmpl);
      updateSquadronFighterCount(target, fullPool / SQUADRON_FIGHTER_COUNT);
    }

    // v3M19 DamageEvent for the floating-damage-numbers renderer.
    // Suppressed if the target is mid-jump (would float over nothing)
    // or if the damageType doesn't match the v3M19 union. Missiles
    // are Concussion sub-type so the union always matches; the EMP
    // guard is defensive against future weapon-type drift.
    if (
      target.systemId !== null &&
      (weapon.damageType === 'Energy' ||
        weapon.damageType === 'Kinetic' ||
        weapon.damageType === 'Concussion')
    ) {
      state.combatEvents.push({
        targetShipId: target.id,
        ownerEmpireId: missile.ownerEmpireId,
        systemId: target.systemId,
        sx: target.sx,
        sy: target.sy,
        shieldDamage: scaledShield,
        hullDamage,
        hitCount: 1,
        missCount: 0,
        damageType: weapon.damageType,
      });
    }

    state.projectiles.delete(id);
  }
}

// v4M9.2.6 — `advanceMissiles` alias removed. All call sites swept to
// `advanceProjectiles` (the canonical name post-v4M9.2.3). The file
// itself retains the `advance-missiles.ts` name; rename to
// `advance-projectiles.ts` is held back to v4M9.7 cleanup to keep
// this checkpoint's diff bounded.

/**
 * v4M9.5 — per-tick beam-sustain advance pass. Walks every entry in
 * `state.beams`, computes the beam's phase, applies sustain damage,
 * and despawns finished or aborted channels.
 *
 * Phases:
 *   - **Charge-up**: `tick < startTick + chargeUpTicks`. Visual lead-in
 *     only; no damage. The renderer can paint a build-up effect during
 *     this window if desired.
 *   - **Sustain**: `startTick + chargeUpTicks <= tick < startTick +
 *     chargeUpTicks + sustainTicks`. `damagePerTick` applies each tick
 *     through the standard shield/armor/hull chain, emitting a
 *     DamageEvent per hit. If `abortOnTargetLost` is true (always in
 *     v1) and the target dies / jumps / changes systems mid-sustain,
 *     the beam despawns silently — no further damage.
 *   - **Done**: `tick >= startTick + chargeUpTicks + sustainTicks`.
 *     Channel despawns.
 *
 * Pipeline position: `advance.ts` calls this immediately after
 * `advanceProjectiles` so projectile arrivals and beam sustain land
 * in the same combat sub-tick. Damage application order within the
 * tick is irrelevant — each beam resolves against its own target
 * independently.
 *
 * Iteration safety: Map iteration is insertion-ordered by spec; we
 * mutate the Map during iteration (delete on despawn). Same safety
 * guarantees as `advanceProjectiles`.
 */
export function advanceBeams(state: GameState): void {
  for (const [id, beam] of state.beams) {
    const age = state.tick - beam.startTick;
    const sustainStart = beam.chargeUpTicks;
    const sustainEnd = beam.chargeUpTicks + beam.sustainTicks;

    // Done — full duration elapsed.
    if (age >= sustainEnd) {
      state.beams.delete(id);
      continue;
    }

    // Charge-up phase — no damage yet, just hold the entry.
    if (age < sustainStart) continue;

    // Sustain phase — validate target + apply per-tick damage.
    const target = state.ships.get(beam.targetShipId);
    if (
      !target ||
      target.currentHull <= 0 ||
      target.systemId !== beam.systemId
    ) {
      // `abortOnTargetLost` is true for every v1 Beam weapon (per the
      // BeamMechanic schema doc); despawn silently when the target is
      // gone. If a future weapon flips the flag, the gate moves here.
      state.beams.delete(id);
      continue;
    }
    // Friendly-fire defence: in v4 teams are locked at game start so
    // a beam's target can't become a teammate mid-sustain, but keep
    // the check defensive (same pattern as projectile arrival).
    if (areTeammates(state, beam.ownerEmpireId, target.ownerEmpireId)) {
      state.beams.delete(id);
      continue;
    }

    const weapon = state.weapons.get(beam.weaponName);
    if (!weapon) {
      state.beams.delete(id);
      continue;
    }

    // Same damage chain as projectile arrival: shields first, then
    // armor-reduced hull, with per-damageType modifiers.
    const targetTmpl = getTemplate(state, target);
    const targetEmpire = state.empires.get(target.ownerEmpireId);
    const armor =
      targetTmpl && targetEmpire
        ? effectiveArmor(state, targetEmpire, targetTmpl)
        : targetTmpl?.armor ?? 0;
    const armorFactor = Math.max(0, Math.min(1, armor));
    const mods = damageTypeModifiers(state.balanceData)[weapon.damageType];

    const damage = beam.damagePerTick;
    const rawShieldHit = Math.min(damage, target.currentShields);
    const rawHullHit = Math.max(0, damage - rawShieldHit);
    const scaledShield = rawShieldHit * mods.shield;
    const scaledPostShield = rawHullHit * mods.hull;
    const hullDamage = scaledPostShield * (1 - armorFactor);

    target.currentShields = Math.max(0, target.currentShields - scaledShield);
    target.currentHull = Math.max(0, target.currentHull - hullDamage);

    // Squadron live-fighter recompute (mirrors the projectile arrival).
    if (target.liveFighterCount !== undefined && targetTmpl && targetEmpire) {
      const fullPool = effectiveMaxHull(state, targetEmpire, targetTmpl);
      updateSquadronFighterCount(target, fullPool / SQUADRON_FIGHTER_COUNT);
    }

    // Per-tick DamageEvent for the floating-damage-numbers renderer.
    // Sustain emits one event per tick — at full sustain length this
    // is sustainTicks events per beam, which is more event-spam than
    // a single projectile hit. v3M11-era polish may want to coalesce
    // these into a single "Beam: total" damage number; for v4M9.5 the
    // straightforward per-tick emission preserves the existing event
    // shape contract.
    if (
      target.systemId !== null &&
      (weapon.damageType === 'Energy' ||
        weapon.damageType === 'Kinetic' ||
        weapon.damageType === 'Concussion')
    ) {
      state.combatEvents.push({
        targetShipId: target.id,
        ownerEmpireId: beam.ownerEmpireId,
        systemId: target.systemId,
        sx: target.sx,
        sy: target.sy,
        shieldDamage: scaledShield,
        hullDamage,
        hitCount: 1,
        missCount: 0,
        damageType: weapon.damageType,
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers (re-exported for tests / future consumers)
// ---------------------------------------------------------------------------

// `DamageEvent` is re-exported for the eventual impact-resolution
// path that emits floating damage numbers. The import lives here so
// the .3 implementation can drop in without re-touching imports.
export type { DamageEvent };

// Same for `damageTypeModifiers` + `effectiveArmor` — the impact
// resolution chain depends on both; declared here so .3 doesn't
// re-import them.
export { damageTypeModifiers, effectiveArmor };
